import type { Expense, Group, Settlement, Transfer, TripData, Unit } from '../types';

/**
 * THE SPLITTING ENGINE
 *
 * Two invariants hold for every input, and the test suite pins both:
 *   1. an expense's shares sum to exactly the expense amount
 *   2. a group's net balances sum to exactly zero
 *
 * Everything is integer minor units. Nothing here knows about currency.
 */

export interface Weighted { id: string; weight: number }
export interface Allocation { id: string; amountMinor: number; remainder: number }

/**
 * Largest-remainder allocation.
 *
 * Splitting 6300 across weights 2 / 2.5 / 3 / 1 does not divide evenly. Floor
 * each exact share, then hand the leftover minor units one at a time to whoever
 * has the largest fractional remainder. Ties break on id so that two devices
 * computing the same split always agree.
 */
export function allocate(totalMinor: number, weights: Weighted[]): Allocation[] {
  const W = weights.reduce((a, b) => a + b.weight, 0);
  if (W <= 0 || totalMinor === 0) {
    return weights.map((w) => ({ id: w.id, amountMinor: 0, remainder: 0 }));
  }
  const parts: Allocation[] = weights.map((w) => {
    const exact = (totalMinor * w.weight) / W;
    const floored = Math.floor(exact);
    return { id: w.id, amountMinor: floored, remainder: exact - floored };
  });
  const leftover = totalMinor - parts.reduce((a, b) => a + b.amountMinor, 0);
  const order = parts
    .slice()
    .sort((a, b) => b.remainder - a.remainder || (a.id < b.id ? -1 : 1));
  for (let i = 0; i < leftover; i++) order[i % order.length].amountMinor++;
  return parts;
}

/** Each household's weight for one expense, under that expense's basis. */
export function unitWeights(units: Unit[], e: Expense): Weighted[] {
  if (e.basis === 'perUnit') {
    return units
      .filter((u) => e.participantUnitIds.includes(u.id))
      .map((u) => ({ id: u.id, weight: 1 }));
  }
  return units
    .map((u) => ({
      id: u.id,
      weight: u.people
        .filter((p) => e.participantPersonIds.includes(p.id))
        .reduce((a, p) => a + p.weight, 0)
    }))
    .filter((w) => w.weight > 0);
}

/** What each household owes for one expense: { unitId: minorUnits }. */
export function sharesFor(units: Unit[], e: Expense): Record<string, number> {
  const out: Record<string, number> = {};
  if (e.basis === 'custom') {
    for (const u of units) {
      const v = e.customMinor?.[u.id];
      if (v) out[u.id] = v;
    }
    return out;
  }
  for (const a of allocate(e.amountMinor, unitWeights(units, e))) {
    if (a.amountMinor) out[a.id] = a.amountMinor;
  }
  return out;
}

export const unitWeightTotal = (u: Unit): number =>
  u.people.reduce((a, p) => a + p.weight, 0);

/**
 * Who actually paid for one expense: { unitId: minorUnits }, summing to exactly
 * the expense amount.
 *
 * Usually the paying household paid it all. When others covered part of it — a
 * coupon or gift card they bought earlier — each is credited with its part and
 * the payer with the rest. The payer's part is derived here rather than stored,
 * so it always adds up to the amount and the zero-sum invariant holds.
 */
export function paymentsFor(e: Expense): Record<string, number> {
  const out: Record<string, number> = { [e.paidByUnitId]: e.amountMinor };
  for (const [id, v] of Object.entries(e.coveredMinor ?? {})) {
    if (!v || id === e.paidByUnitId) continue;
    out[id] = v;
    out[e.paidByUnitId] -= v;
  }
  return out;
}

/** Households other than the payer that covered part of an expense, in household order. */
export function coverersOf(units: Unit[], e: Expense): Unit[] {
  const pay = paymentsFor(e);
  return units.filter((u) => u.id !== e.paidByUnitId && pay[u.id]);
}

/**
 * Net position per household. Positive means the group owes them.
 *
 * Because every figure is keyed by household, two people in the same household
 * can never owe each other — it isn't prevented, it's unrepresentable.
 */
export function balances(
  units: Unit[],
  expenses: Expense[],
  settlements: Settlement[]
): Record<string, number> {
  const net: Record<string, number> = {};
  for (const u of units) net[u.id] = 0;

  for (const e of expenses) {
    const pay = paymentsFor(e);
    for (const id of Object.keys(pay)) {
      if (net[id] !== undefined) net[id] += pay[id];
    }
    const sh = sharesFor(units, e);
    for (const id of Object.keys(sh)) {
      if (net[id] !== undefined) net[id] -= sh[id];
    }
  }
  for (const s of settlements) {
    if (net[s.fromUnitId] !== undefined) net[s.fromUnitId] += s.amountMinor;
    if (net[s.toUnitId] !== undefined) net[s.toUnitId] -= s.amountMinor;
  }
  return net;
}

/**
 * Fewest transfers that clear everyone: repeatedly match the largest debtor to
 * the largest creditor. Produces at most n-1 payments instead of everyone
 * paying everyone.
 */
export function simplify(net: Record<string, number>): Transfer[] {
  const creditors: { id: string; amt: number }[] = [];
  const debtors: { id: string; amt: number }[] = [];
  for (const id of Object.keys(net)) {
    if (net[id] > 0) creditors.push({ id, amt: net[id] });
    else if (net[id] < 0) debtors.push({ id, amt: -net[id] });
  }
  const byAmt = (a: { id: string; amt: number }, b: { id: string; amt: number }) =>
    b.amt - a.amt || (a.id < b.id ? -1 : 1);
  creditors.sort(byAmt);
  debtors.sort(byAmt);

  const out: Transfer[] = [];
  let i = 0;
  let j = 0;
  while (i < debtors.length && j < creditors.length) {
    const amt = Math.min(debtors[i].amt, creditors[j].amt);
    if (amt > 0) out.push({ from: debtors[i].id, to: creditors[j].id, amountMinor: amt });
    debtors[i].amt -= amt;
    creditors[j].amt -= amt;
    if (debtors[i].amt === 0) i++;
    if (creditors[j].amt === 0) j++;
  }
  return out;
}

export interface TripFacts {
  net: Record<string, number>;
  plan: Transfer[];
  rows: { expense: Expense; shares: Record<string, number> }[];
  paid: Record<string, number>;
  share: Record<string, number>;
  sentOut: Record<string, number>;
  received: Record<string, number>;
  hid: Record<string, string>;
  totalMinor: number;
}

/**
 * One pass over a trip, used by every screen and every exporter, so an export
 * can never disagree with what the app displayed.
 */
export function tripFacts(data: TripData): TripFacts {
  const { units, expenses, settlements } = data;
  const net = balances(units, expenses, settlements);
  const plan = simplify(net);

  const rows = expenses
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id))
    .map((expense) => ({ expense, shares: sharesFor(units, expense) }));

  const paid: Record<string, number> = {};
  const share: Record<string, number> = {};
  const sentOut: Record<string, number> = {};
  const received: Record<string, number> = {};
  const hid: Record<string, string> = {};
  units.forEach((u, i) => {
    paid[u.id] = 0;
    share[u.id] = 0;
    sentOut[u.id] = 0;
    received[u.id] = 0;
    hid[u.id] = 'H' + (i + 1);
  });

  for (const { expense, shares } of rows) {
    const pay = paymentsFor(expense);
    for (const id of Object.keys(pay)) {
      if (paid[id] !== undefined) paid[id] += pay[id];
    }
    for (const id of Object.keys(shares)) {
      if (share[id] !== undefined) share[id] += shares[id];
    }
  }
  for (const s of settlements) {
    if (sentOut[s.fromUnitId] !== undefined) sentOut[s.fromUnitId] += s.amountMinor;
    if (received[s.toUnitId] !== undefined) received[s.toUnitId] += s.amountMinor;
  }

  return {
    net,
    plan,
    rows,
    paid,
    share,
    sentOut,
    received,
    hid,
    totalMinor: expenses.reduce((a, e) => a + e.amountMinor, 0)
  };
}

/** The household a given signed-in user acts as, if any. */
export const myUnit = (units: Unit[], uid: string | undefined): Unit | undefined =>
  uid ? units.find((u) => u.linkedUids.includes(uid)) : undefined;

export const defaultBasisOf = (g: Group): Expense['basis'] => g.defaultBasis || 'perPerson';
